import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen, Zap, Music, FolderOpen, ListMusic, Cloud, Upload,
  Smartphone, RefreshCw, Key, BarChart3, Headphones,
  ArrowRight, ChevronDown, ChevronUp, Shield, Settings,
  Play, Pause, SkipForward, Search, Palette, X
} from 'lucide-react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const sections = [
  {
    id: 'overview',
    title: 'Overview',
    icon: Zap,
    content: `CRATELAB is a professional DJ track management platform that lives entirely in your browser.

**Upload & Analyze** — Drop in your MP3s and CRATELAB automatically analyzes BPM, Camelot key, energy, and structure server-side. No software to install.

**Web App** — Your cloud library. View every track, stream audio, generate AI playlists, manage crates and playlists, and get coaching from the AI DJ assistant — all from any browser.`
  },
  {
    id: 'architecture',
    title: 'Platform Architecture',
    icon: Cloud,
    diagram: 'architecture',
    content: `CRATELAB is a fully cloud-based platform. You upload tracks via the browser, analysis runs on the server, and your library is accessible from any device.`
  },
  {
    id: 'desktop',
    title: 'Uploading Tracks',
    icon: Upload,
    content: `Uploading tracks to CRATELAB is straightforward — no software required.

**Steps:**
1. Click **Upload** in the sidebar or the **Import Tracks** button in the top header
2. Drag and drop your MP3 files, or click to browse
3. CRATELAB uploads the files to Cloudflare R2 cloud storage
4. Analysis runs automatically on the server (BPM, Key, Energy, Structure)
5. Tracks appear in your Library within seconds

**Supported Formats:**
MP3 files are fully supported. ID3 tags (title, artist, album art) are read automatically on upload.

**Credits:**
Each upload consumes a small credit based on file size. AI analysis costs 2 credits per track.`
  },
  {
    id: 'analysis',
    title: 'Track Analysis',
    icon: BarChart3,
    diagram: 'analysis',
    content: `When you upload a track, CRATELAB runs a comprehensive analysis pipeline server-side using Python/librosa.

**BPM Detection** — Calculates the tempo in beats per minute. Essential for beatmatching.

**Camelot Key** — Detects the musical key and converts it to the Camelot Wheel notation (e.g., 8A, 11B). DJs use this to mix tracks in harmonically compatible keys.

**Energy Score** — Rates the track's energy level from 0-100 based on loudness, spectral complexity, and dynamic range.

**Structure Mapping** — Identifies sections: Intro, Verse, Build, Drop, Outro. Each section includes start/end times and energy levels. Visible in the waveform player as colored segments.

**Cue Points** — Marks suggested mix-in and mix-out points based on structure analysis.`
  },
  {
    id: 'camelot',
    title: 'Camelot Wheel & Mixing',
    icon: Key,
    diagram: 'camelot',
    content: `The Camelot Wheel is the DJ's harmonic mixing tool. It maps musical keys to numbered positions on a wheel, making it easy to find compatible tracks.

**How it works:**
- Each key gets a number (1-12) and letter (A for minor, B for major)
- Mix tracks with the **same number** (e.g., 8A → 8A)
- Mix tracks **one position up or down** (e.g., 8A → 7A or 9A)
- Mix between **relative major/minor** (e.g., 8A → 8B)

**Compatible Tracks Feature:**
When viewing a track's detail page, CRATELAB automatically shows all harmonically compatible tracks from your library. These are tracks that will sound good when mixed together.`
  },
  {
    id: 'library',
    title: 'Library Management',
    icon: Music,
    content: `Your Library is the central hub showing all synced tracks.

**Features:**
- **Sort & Filter** — Sort by title, artist, BPM, key, energy, or date added
- **Search** — Full-text search across title and artist
- **Track Details** — Click any track to see full analysis, waveform, structure, compatible tracks, and cue points
- **Cloud Playback** — Stream any synced track directly in the browser
- **Key Analysis** — Trigger a fresh analysis of any track

**Track Status:**
- Tracks with a play button have audio available in the cloud
- Tracks without audio can be re-uploaded via the Upload page`
  },
  {
    id: 'crates',
    title: 'Crates',
    icon: FolderOpen,
    content: `Crates are flexible containers for organizing your tracks. Think of them as folders or tags.

**How to use Crates:**
1. Create a new crate with a name, description, and color
2. Add tracks to crates from the Library or Track Detail page
3. A track can belong to multiple crates
4. Removing a track from a crate does NOT delete it from your library

**Organization Ideas:**
- By genre: "Tech House", "Drum & Bass", "Deep House"
- By event: "Friday Residency", "Festival Set"
- By mood: "Warm Up", "Peak Time", "After Hours"
- By BPM range: "120-125", "130-140"`
  },
  {
    id: 'playlists',
    title: 'Playlists',
    icon: ListMusic,
    content: `Playlists are ordered sequences of tracks — your DJ sets.

**Manual Playlists:**
- Create a playlist and add tracks in your preferred order
- Drag and drop to reorder tracks
- Remove tracks without deleting them from your library

**AI-Generated Playlists:**
CRATELAB can automatically generate playlists using the Camelot Wheel for harmonic compatibility.

**AI Generation Options:**
- **Playlist Name** — Name your generated mix
- **Start Track** — Pick the opening track (optional)
- **BPM Range** — Filter tracks to a specific tempo range
- **Max Tracks** — Up to 20 tracks per generated playlist
- **Energy Level** — Filter by energy range

The AI scores each potential next track based on:
- Key compatibility (Camelot match = highest score)
- BPM proximity (within 3% = best)
- Already-used tracks are excluded`
  },
  {
    id: 'sync',
    title: 'Cloud Storage',
    icon: Cloud,
    diagram: 'sync',
    content: `CRATELAB stores all your music and data securely in the cloud.

**What gets stored:**
- Track metadata (title, artist, album, genre)
- Analysis data (BPM, key, energy, structure, cue points)
- Audio files (MP3s uploaded to Cloudflare R2 storage)
- Crates and playlists (names, track assignments, ordering)

**How uploads work:**
1. **Upload** — Select your MP3 files in the Upload page
2. **Cloud Storage** — Files are chunked and uploaded to Cloudflare R2
3. **Analysis** — Server analyzes BPM, key, energy, and structure
4. **Verification** — Upload verified — track marked as playable in your library
5. **Streaming** — Stream audio directly from R2 in any browser

**Access Anywhere:** Once uploaded, your library is available on any device where you log in.`
  },
  {
    id: 'player',
    title: 'Audio Player',
    icon: Headphones,
    content: `The Web App includes a full-featured cloud audio player.

**Controls:**
- Play/Pause, Skip Forward/Back (10s), Previous/Next track
- Click anywhere on the progress bar to seek
- Volume control with mute toggle
- Auto-advances to the next track in a playlist

**Waveform Display:**
On the Track Detail page, an interactive waveform (powered by WaveSurfer.js) shows the full audio visualization. Structure sections are color-coded:
- **Red** = Drop (high energy)
- **Yellow** = Build
- **Gray** = Intro/Verse/Outro

**Lock Screen Controls:**
On Android, playback controls appear on the lock screen via the Media Session API. Track title and artist are displayed in the notification area.

**Background Playback:**
Audio continues playing when the browser is minimized or the phone screen is off (via Bluetooth/wired headphones).`
  },
  {
    id: 'accounts',
    title: 'Accounts & Security',
    icon: Shield,
    content: `**Registration:**
- Sign up with email and password
- Password must be 8+ characters with uppercase, lowercase, number, and special character
- A verification email is sent to activate your account
- You can log in before verifying, but you'll only see a "Verify Email" screen

**Login:**
- Email + password authentication
- "Remember Me" extends your session from 24 hours to 30 days
- Eye icon to toggle password visibility

**Password Reset:**
- Click "Forgot Password" on the login page
- Enter your registered email
- Check your inbox for a reset link from noreply@cratelab.io
- Set a new password (same strength requirements)

**Admin Accounts:**
Admins have a separate dashboard showing all registered users, track/playlist counts, activity feeds, and the ability to freeze or delete user accounts.`
  },
  {
    id: 'mobile',
    title: 'Mobile Experience',
    icon: Smartphone,
    content: `The Web App is fully responsive and works on any mobile browser.

**Mobile Features:**
- Full-screen navigation menu
- Touch-friendly controls
- Audio playback with Bluetooth support
- PWA installable (Android: "Add to Home Screen")

**iOS Notes:**
- Audio plays in the background when connected to Bluetooth
- The app icon can be added to your home screen via Safari's Share menu
- Lock screen controls available on supported versions`
  }
];

function ArchitectureDiagram() {
  return (
    <div className="my-6 p-4 sm:p-6 bg-white/50 border border-[#E2E8F0]">
      {/* Mobile: Vertical stack */}
      <div className="sm:hidden space-y-3">
        <div className="border border-blue-500/50 bg-blue-950/20 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Upload className="w-4 h-4 text-blue-400" />
            <span className="font-heading text-xs text-blue-400">WEB UPLOAD</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {['React Frontend', 'MP3 Files', 'Browser-based', 'No install needed'].map(t => (
              <span key={t} className="bg-blue-950/30 border border-blue-900/30 px-2 py-1 text-[10px] font-mono text-blue-300">{t}</span>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 py-1">
          <span className="text-[#2563FF]">&#8595;</span>
          <span className="text-[10px] font-mono text-[#64748B]">UPLOAD + ANALYZE</span>
          <span className="text-[#2563FF]">&#8595;</span>
        </div>
        <div className="border border-purple-500/50 bg-purple-950/20 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Cloud className="w-4 h-4 text-purple-400" />
            <span className="font-heading text-xs text-purple-400">CLOUD</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {['MongoDB Atlas', 'Cloudflare R2', 'FastAPI'].map(t => (
              <span key={t} className="bg-purple-950/30 border border-purple-900/30 px-2 py-1 text-[10px] font-mono text-purple-300">{t}</span>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 py-1">
          <span className="text-green-500">&#8595;</span>
          <span className="text-[10px] font-mono text-[#64748B]">READ stream + data</span>
          <span className="text-green-500">&#8595;</span>
        </div>
        <div className="border border-green-500/50 bg-green-950/20 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Smartphone className="w-4 h-4 text-green-400" />
            <span className="font-heading text-xs text-green-400">WEB APP</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {['React', 'Cloud Playback', 'AI Playlists', 'Mobile'].map(t => (
              <span key={t} className="bg-green-950/30 border border-green-900/30 px-2 py-1 text-[10px] font-mono text-green-300">{t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop: Horizontal layout */}
      <div className="hidden sm:block">
        <div className="flex items-start justify-between gap-4 min-w-[500px]">
          <div className="flex-1 border border-blue-500/50 bg-blue-950/20 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Upload className="w-5 h-5 text-blue-400" />
              <span className="font-heading text-sm text-blue-400">WEB UPLOAD</span>
            </div>
            <div className="space-y-2">
              {['React Frontend', 'MP3 File Upload', 'Browser-based', 'No install needed'].map(t => (
                <div key={t} className="bg-blue-950/30 border border-blue-900/30 p-2 text-xs font-mono text-blue-300">{t}</div>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-center justify-center gap-2 pt-12">
            <div className="text-xs font-mono text-[#64748B]">UPLOAD</div>
            <div className="text-[#2563FF] text-lg">&#8594;</div>
            <div className="text-xs font-mono text-[#94A3B8]">audio</div>
            <div className="text-[#2563FF] text-lg">&#8594;</div>
            <div className="text-xs font-mono text-[#94A3B8]">metadata</div>
          </div>
          <div className="flex-1 border border-purple-500/50 bg-purple-950/20 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Cloud className="w-5 h-5 text-purple-400" />
              <span className="font-heading text-sm text-purple-400">CLOUD</span>
            </div>
            <div className="space-y-2">
              {['MongoDB Atlas', 'Cloudflare R2', 'FastAPI Backend'].map(t => (
                <div key={t} className="bg-purple-950/30 border border-purple-900/30 p-2 text-xs font-mono text-purple-300">{t}</div>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-center justify-center gap-2 pt-12">
            <div className="text-xs font-mono text-[#64748B]">READ</div>
            <div className="text-green-500 text-lg">&#8592;</div>
            <div className="text-xs font-mono text-[#94A3B8]">stream</div>
            <div className="text-green-500 text-lg">&#8592;</div>
            <div className="text-xs font-mono text-[#94A3B8]">data</div>
          </div>
          <div className="flex-1 border border-green-500/50 bg-green-950/20 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Smartphone className="w-5 h-5 text-green-400" />
              <span className="font-heading text-sm text-green-400">WEB APP</span>
            </div>
            <div className="space-y-2">
              {['React Frontend', 'Cloud Playback', 'AI Playlists', 'Mobile + Desktop'].map(t => (
                <div key={t} className="bg-green-950/30 border border-green-900/30 p-2 text-xs font-mono text-green-300">{t}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnalysisDiagram() {
  const steps = [
    { label: 'MP3 Import', color: 'text-blue-400', bg: 'bg-blue-950/30 border-blue-900/30' },
    { label: 'BPM Detection', color: 'text-yellow-400', bg: 'bg-yellow-950/30 border-yellow-900/30' },
    { label: 'Key Detection', color: 'text-green-400', bg: 'bg-green-950/30 border-green-900/30' },
    { label: 'Energy Scoring', color: 'text-orange-400', bg: 'bg-orange-950/30 border-orange-900/30' },
    { label: 'Structure Map', color: 'text-purple-400', bg: 'bg-purple-950/30 border-purple-900/30' },
    { label: 'Cue Points', color: 'text-[#2563FF]', bg: 'bg-[#EFF6FF] border-[#E2E8F0]' },
  ];
  return (
    <div className="my-6 p-4 sm:p-6 bg-white/50 border border-[#E2E8F0]">
      <div className="flex flex-wrap items-center justify-center gap-2">
        {steps.map((step, i) => (
          <div key={step.label} className="flex items-center gap-2">
            <div className={`${step.bg} border px-3 py-2 font-mono text-xs ${step.color}`}>
              {step.label}
            </div>
            {i < steps.length - 1 && <ArrowRight className="w-4 h-4 text-[#94A3B8] hidden sm:block" />}
          </div>
        ))}
      </div>
      <div className="mt-4 text-center">
        <div className="inline-flex flex-wrap justify-center gap-x-3 gap-y-1 bg-[#EFF6FF] border border-[#E2E8F0] px-4 py-2">
          <span className="font-mono text-[10px] sm:text-xs text-[#64748B]">Output:</span>
          <span className="font-mono text-[10px] sm:text-xs text-[#2563FF]">128 BPM</span>
          <span className="font-mono text-[10px] sm:text-xs text-green-400">8A</span>
          <span className="font-mono text-[10px] sm:text-xs text-yellow-400">Energy: 78</span>
          <span className="font-mono text-[10px] sm:text-xs text-purple-400">5 Sections</span>
        </div>
      </div>
    </div>
  );
}

function CamelotDiagram() {
  const wheel = [
    { num: '1', minor: 'Abm', major: 'B' },
    { num: '2', minor: 'Ebm', major: 'F#' },
    { num: '3', minor: 'Bbm', major: 'Db' },
    { num: '4', minor: 'Fm', major: 'Ab' },
    { num: '5', minor: 'Cm', major: 'Eb' },
    { num: '6', minor: 'Gm', major: 'Bb' },
    { num: '7', minor: 'Dm', major: 'F' },
    { num: '8', minor: 'Am', major: 'C' },
    { num: '9', minor: 'Em', major: 'G' },
    { num: '10', minor: 'Bm', major: 'D' },
    { num: '11', minor: 'F#m', major: 'A' },
    { num: '12', minor: 'Dbm', major: 'E' },
  ];
  return (
    <div className="my-6 p-4 sm:p-6 bg-white/50 border border-[#E2E8F0] overflow-x-auto">
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5 sm:gap-2">
        {wheel.map((key) => (
          <div key={key.num} className="text-center">
            <div className="bg-blue-950/30 border border-blue-900/30 p-2 mb-1">
              <span className="font-mono text-sm text-blue-400 font-bold">{key.num}A</span>
              <p className="font-mono text-[10px] text-[#64748B]">{key.minor}</p>
            </div>
            <div className="bg-[#EFF6FF] border border-[#E2E8F0] p-2">
              <span className="font-mono text-sm text-[#2563FF] font-bold">{key.num}B</span>
              <p className="font-mono text-[10px] text-[#64748B]">{key.major}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap justify-center gap-2 sm:gap-4 text-[10px] sm:text-xs font-mono">
        <span className="text-green-400">Same number = Compatible</span>
        <span className="text-yellow-400">+/-1 number = Compatible</span>
        <span className="text-blue-400">A ↔ B = Relative major/minor</span>
      </div>
    </div>
  );
}

function SyncDiagram() {
  return (
    <div className="my-6 p-4 sm:p-6 bg-white/50 border border-[#E2E8F0]">
      <div className="space-y-3">
        {[
          { step: '1', label: 'Select MP3 files on the Upload page', icon: Upload, color: 'text-blue-400' },
          { step: '2', label: 'Audio uploaded to Cloudflare R2 storage', icon: Cloud, color: 'text-purple-400' },
          { step: '3', label: 'Metadata saved to MongoDB Atlas', icon: BarChart3, color: 'text-green-400' },
          { step: '4', label: 'Server analyzes BPM, key, energy & structure', icon: RefreshCw, color: 'text-yellow-400' },
          { step: '5', label: 'Track marked as playable — stream from any browser', icon: Play, color: 'text-[#2563FF]' },
        ].map((item) => (
          <div key={item.step} className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#EFF6FF] border border-[#E2E8F0] flex items-center justify-center flex-shrink-0">
              <span className="font-mono text-xs text-[#2563FF]">{item.step}</span>
            </div>
            <item.icon className={`w-4 h-4 ${item.color} flex-shrink-0`} />
            <span className="font-mono text-xs sm:text-sm text-[#334155]">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const diagrams = {
  architecture: ArchitectureDiagram,
  analysis: AnalysisDiagram,
  camelot: CamelotDiagram,
  sync: SyncDiagram,
};

export default function DocsPage() {
  useDocumentTitle('Documentation - How CRATELAB Works');
  const [activeSection, setActiveSection] = useState('overview');
  const [expandedMobile, setExpandedMobile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSections = searchQuery.trim()
    ? sections.filter(s =>
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.content.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sections;

  const currentSection = (searchQuery.trim() && filteredSections.length > 0)
    ? filteredSections[0]
    : sections.find(s => s.id === activeSection);
  const DiagramComponent = currentSection?.diagram ? diagrams[currentSection.diagram] : null;

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-6 h-6 text-[#2563FF]" />
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] tracking-tight"><span className="gradient-text">Documentation</span></h1>
          </div>
          <p className="font-body text-sm text-[#64748B]">Everything you need to know about CRATELAB</p>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search docs... (e.g. BPM, Camelot, sync, playlist)"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#E2E8F0] text-[#0F172A] text-sm font-body placeholder:text-[#94A3B8] focus:border-[#2563FF]/50 focus:outline-none"
            data-testid="docs-search"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0F172A]"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Search results hint */}
        {searchQuery.trim() && (
          <p className="font-mono text-xs text-[#64748B] mb-4">
            {filteredSections.length === 0
              ? 'No results found'
              : `${filteredSections.length} section${filteredSections.length !== 1 ? 's' : ''} matching "${searchQuery}"`
            }
          </p>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar - Desktop */}
          <nav className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-1">
              {filteredSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => { setActiveSection(section.id); setSearchQuery(''); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left font-heading text-xs uppercase tracking-wider transition-colors ${
                    currentSection?.id === section.id
                      ? 'text-[#2563FF] bg-[#EFF6FF] border-l-2 border-[#2563FF]'
                      : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8FAFC] border-l-2 border-transparent'
                  }`}
                  data-testid={`docs-nav-${section.id}`}
                >
                  <section.icon className="w-4 h-4" />
                  {section.title}
                </button>
              ))}
            </div>
          </nav>

          {/* Content - Desktop */}
          <div className="hidden lg:block flex-1 min-w-0">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white border border-[#E2E8F0] p-6 sm:p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                {currentSection && <currentSection.icon className="w-6 h-6 text-[#2563FF]" />}
                <h2 className="font-heading text-lg sm:text-xl text-[#0F172A]">{currentSection?.title}</h2>
              </div>

              {DiagramComponent && <DiagramComponent />}

              <div className="prose-custom">
                {currentSection?.content.split('\n\n').map((paragraph, i) => (
                  <div key={i} className="mb-4">
                    {paragraph.split('\n').map((line, j) => {
                      if (line.startsWith('**') && line.endsWith('**')) {
                        return <h3 key={j} className="font-heading text-sm text-[#2563FF] uppercase tracking-wider mt-6 mb-2">{line.replace(/\*\*/g, '')}</h3>;
                      }
                      if (line.startsWith('**')) {
                        const parts = line.split('**');
                        return (
                          <p key={j} className="font-body text-sm text-[#334155] mb-1">
                            {parts.map((part, k) => k % 2 === 1 ? <strong key={k} className="text-[#0F172A]">{part}</strong> : part)}
                          </p>
                        );
                      }
                      if (line.startsWith('- ')) {
                        const text = line.substring(2);
                        const parts = text.split('**');
                        return (
                          <div key={j} className="flex items-start gap-2 mb-1 ml-2">
                            <span className="text-[#2563FF] mt-1.5 text-xs">&#9656;</span>
                            <p className="font-body text-sm text-[#334155]">
                              {parts.map((part, k) => k % 2 === 1 ? <strong key={k} className="text-[#0F172A]">{part}</strong> : part)}
                            </p>
                          </div>
                        );
                      }
                      if (line.match(/^\d+\./)) {
                        const text = line.replace(/^\d+\.\s*/, '');
                        const parts = text.split('**');
                        return (
                          <div key={j} className="flex items-start gap-2 mb-1 ml-2">
                            <span className="font-mono text-xs text-[#2563FF] mt-0.5 w-4">{line.match(/^\d+/)[0]}.</span>
                            <p className="font-body text-sm text-[#334155]">
                              {parts.map((part, k) => k % 2 === 1 ? <strong key={k} className="text-[#0F172A]">{part}</strong> : part)}
                            </p>
                          </div>
                        );
                      }
                      return line.trim() ? <p key={j} className="font-body text-sm text-[#334155] mb-1">{line}</p> : null;
                    })}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Mobile - Accordion */}
          <div className="lg:hidden space-y-2">
            {filteredSections.map((section) => {
              const isExpanded = expandedMobile === section.id;
              const SectionDiagram = section.diagram ? diagrams[section.diagram] : null;
              return (
                <div key={section.id} className="bg-white border border-[#E2E8F0]">
                  <button
                    onClick={() => setExpandedMobile(isExpanded ? null : section.id)}
                    className="w-full flex items-center justify-between p-4"
                  >
                    <div className="flex items-center gap-2.5">
                      <section.icon className="w-4 h-4 text-[#2563FF]" />
                      <span className="font-heading text-sm text-[#0F172A] uppercase tracking-wider">{section.title}</span>
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-[#64748B]" /> : <ChevronDown className="w-4 h-4 text-[#64748B]" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4">
                      {SectionDiagram && <SectionDiagram />}
                      {section.content.split('\n\n').map((paragraph, i) => (
                        <div key={i} className="mb-3">
                          {paragraph.split('\n').map((line, j) => {
                            if (line.startsWith('**') && line.endsWith('**')) {
                              return <h3 key={j} className="font-heading text-xs text-[#2563FF] uppercase tracking-wider mt-4 mb-1">{line.replace(/\*\*/g, '')}</h3>;
                            }
                            if (line.startsWith('**')) {
                              const parts = line.split('**');
                              return <p key={j} className="font-body text-xs text-[#334155] mb-1">{parts.map((part, k) => k % 2 === 1 ? <strong key={k} className="text-[#0F172A]">{part}</strong> : part)}</p>;
                            }
                            if (line.startsWith('- ')) {
                              const text = line.substring(2);
                              const parts = text.split('**');
                              return <div key={j} className="flex items-start gap-1.5 mb-1 ml-1"><span className="text-[#2563FF] mt-1 text-[10px]">&#9656;</span><p className="font-body text-xs text-[#334155]">{parts.map((part, k) => k % 2 === 1 ? <strong key={k} className="text-[#0F172A]">{part}</strong> : part)}</p></div>;
                            }
                            if (line.match(/^\d+\./)) {
                              const text = line.replace(/^\d+\.\s*/, '');
                              const parts = text.split('**');
                              return <div key={j} className="flex items-start gap-1.5 mb-1 ml-1"><span className="font-mono text-[10px] text-[#2563FF] mt-0.5">{line.match(/^\d+/)[0]}.</span><p className="font-body text-xs text-[#334155]">{parts.map((part, k) => k % 2 === 1 ? <strong key={k} className="text-[#0F172A]">{part}</strong> : part)}</p></div>;
                            }
                            return line.trim() ? <p key={j} className="font-body text-xs text-[#334155] mb-1">{line}</p> : null;
                          })}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
